<template>
  <div class="health-record-container">
    <div class="page-title">健康台账</div>
    
    <!-- 搜索筛选区域 -->
    <div class="filter-section">
      <div class="filter-row">
        <el-date-picker
          v-model="filters.timeRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          value-format="yyyy-MM-dd"
          class="date-picker-input">
        </el-date-picker>
        
        <el-input v-model="filters.userName" placeholder="姓名" class="filter-item"></el-input>
        
        <el-select v-model="filters.status" placeholder="全部状态" class="filter-select">
          <el-option label="全部状态" value=""></el-option>
          <el-option label="待审核" value="1"></el-option>
          <el-option label="审核通过" value="2"></el-option>
          <el-option label="审核驳回" value="9"></el-option>
        </el-select>
        
        <el-button type="primary" plain @click="handleSearch" class="filter-button">查询</el-button>
        <el-button plain @click="handleReset" class="filter-button">重置</el-button>
        <div class="spacer"></div>
        <el-button plain @click="handleExport" icon="el-icon-download">导出</el-button>
      </div>
    </div>
    
    <!-- 数据表格 -->
    <el-table :data="tableData" border stripe style="width: 100%">
      <el-table-column prop="formName" label="填报模版名称" width="150" align="center"></el-table-column>
      <el-table-column prop="name" label="填报人" width="100" align="center"></el-table-column>
      <el-table-column prop="department" label="所在部门" width="120" align="center"></el-table-column>
      <el-table-column prop="team" label="所在班组" width="120" align="center"></el-table-column>
      <el-table-column prop="fillTypeName" label="填报类型" width="100" align="center"></el-table-column>
      <el-table-column prop="healthStatus" label="健康评级" width="100" align="center">
        <template slot-scope="scope">
          <span :class="{ 'status-normal': scope.row.healthStatus === '正常' }">
            {{ scope.row.healthStatus }}
          </span>
        </template>
      </el-table-column>
      <el-table-column prop="measureDetail" label="排查情况" min-width="180" align="center"></el-table-column>
      <el-table-column prop="controlMeasure" label="管控措施" min-width="180" align="center"></el-table-column>
      <el-table-column prop="healthScore" label="健康评分" width="100" align="center"></el-table-column>
      <el-table-column prop="reportStatus" label="填报状态" width="100" align="center">
        <template slot-scope="scope">
          <span :class="getStatusClass(scope.row.reportStatus)">
            {{ scope.row.reportStatus }}
          </span>
        </template>
      </el-table-column>
      <el-table-column prop="reportTime" label="填报时间" width="180" align="center"></el-table-column>
      <el-table-column label="操作" width="100" align="center">
        <template slot-scope="scope">
          <el-button type="text" @click="handleViewDetail(scope.row)">查看详情</el-button>
        </template>
      </el-table-column>
    </el-table>
    
    <!-- 分页 -->
    <div class="pagination-container">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pagination.currentPage"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pagination.pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="pagination.total">
      </el-pagination>
    </div>
    
    <!-- 健康填报详情弹窗 -->
    <el-dialog
      title="每日健康填报详情"
      :visible.sync="detailDialogVisible"
      width="50%"
      :before-close="handleCloseDetailDialog"
      class="health-detail-dialog">
      <div class="detail-content">
        <h3 class="section-title">基本信息</h3>
        <div class="detail-row">
          <div class="detail-item">
            <span class="detail-label">所在班组</span>
            <span class="detail-value">{{ currentDetail.team || currentDetail.className || '' }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">姓名</span>
            <span class="detail-value">{{ currentDetail.name || currentDetail.userName || '' }}</span>
          </div>
        </div>
        <div class="detail-row">
          <div class="detail-item">
            <span class="detail-label">填报方式</span>
            <span class="detail-value">{{ currentDetail.fillTypeName || (currentDetail.fillType === 2 ? '按班组' : '按天') }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">健康评级</span>
            <span class="detail-value">{{ currentDetail.healthStatus || currentDetail.healthRatingName || '' }}</span>
          </div>
        </div>
        <div class="detail-row">
          <div class="detail-item">
            <span class="detail-label">填报时间</span>
            <span class="detail-value">{{ currentDetail.createTime ? formatDateTime(currentDetail.createTime) : '' }}</span>
          </div>
        </div>
        <div class="detail-row" v-if="currentDetail.approvedUserName">
          <div class="detail-item">
            <span class="detail-label">审核人</span>
            <span class="detail-value">{{ currentDetail.approvedUserName || '' }}</span>
          </div>
        </div>
        <div class="detail-row" v-if="currentDetail.approvedTime">
          <div class="detail-item">
            <span class="detail-label">审核时间</span>
            <span class="detail-value">{{ currentDetail.approvedTime ? formatDateTime(currentDetail.approvedTime) : '' }}</span>
          </div>
        </div>

        <h3 class="section-title">健康评分</h3>
        <div class="detail-row">
          <div class="detail-item full-width">
            <span class="detail-value score">{{ (currentDetail.score !== undefined && currentDetail.score !== null) ? currentDetail.score : ((currentDetail.totalScore !== undefined && currentDetail.totalScore !== null) ? currentDetail.totalScore : '-') }}</span>
          </div>
        </div>

        <h3 class="section-title">排查状况 <span class="required">*</span></h3>
        <div v-if="currentDetail.formFields && currentDetail.formFields.length">
          <div class="detail-row" v-for="(field, index) in currentDetail.formFields" :key="'field-'+index">
            <div class="detail-item full-width">
              <span class="detail-label">{{ field.formFieldName }}</span>
              <span class="detail-value">
                <!-- 其他类型字段 -->
                {{ field.formFieldOptionValue || field.optionName || field.optionValue || field.value || '-' }}
              </span>
            </div>
          </div>
        </div>
        <div v-else>
          <div class="detail-row">
            <div class="detail-item full-width">
              <span class="detail-value">{{ currentDetail.measureDetail || '暂无数据' }}</span>
            </div>
          </div>
        </div>

        <h3 class="section-title">管控措施 <span class="required">*</span></h3>
        <div v-if="currentDetail.formMeasures && currentDetail.formMeasures.length">
          <div class="detail-row" v-for="(measure, index) in currentDetail.formMeasures" :key="'measure-'+index">
            <div class="detail-item full-width">
              <span class="detail-value">{{ measure.measureName }}</span>
            </div>
          </div>
          
          <div class="detail-row" v-if="currentDetail.otherMeasure">
            <div class="detail-item full-width">
              <div class="detail-column">
                <span class="detail-label-top">其他管控措施</span>
                <div class="detail-textarea">{{ currentDetail.otherMeasure }}</div>
              </div>
            </div>
          </div>
        </div>
        <div v-else-if="currentDetail.otherMeasure">
          <div class="detail-row">
            <div class="detail-item full-width">
              <div class="detail-column">
                <span class="detail-label-top">其他管控措施</span>
                <div class="detail-textarea">{{ currentDetail.otherMeasure }}</div>
              </div>
            </div>
          </div>
        </div>
        <div v-else>
          <div class="detail-row">
            <div class="detail-item full-width">
              <span class="detail-value">暂无数据</span>
            </div>
          </div>
        </div>

        <h3 class="section-title">填报流程</h3>
        <div class="process-timeline">
          <div class="process-item">
            <div class="process-time">{{ currentDetail.createTime ? formatDateTime(currentDetail.createTime) : '' }}</div>
            <div class="process-user">{{ currentDetail.name || currentDetail.userName || '' }}</div>
            <div class="process-action">提交</div>
          </div>
          <div class="process-item" v-if="currentDetail.approvedTime">
            <div class="process-time">{{ formatDateTime(currentDetail.approvedTime) }}</div>
            <div class="process-user">{{ currentDetail.approvedUserName || '' }}</div>
            <div class="process-action">{{ currentDetail.status === 2 ? '审批通过' : '审批驳回' }}</div>
          </div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="detailDialogVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getHealthRecordPage, getHealthFormUserDetail } from '@/api/health-form-user'

export default {
  name: 'HealthRecord',
  data() {
    return {
      // 筛选条件
      filters: {
        timeRange: '', // 时间范围
        userName: '', // 姓名
        status: '', // 状态
        createTimeStart: '',
        createTimeEnd: '',
        pageNum: 1,
        pageSize: 10
      },
      
      // 表格数据
      tableData: [],
      
      // 分页数据
      pagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      },
      
      // 详情弹窗
      detailDialogVisible: false,
      currentDetail: {},
      
      // 加载状态
      loading: {
        table: false,
        detail: false,
        export: false
      }
    }
  },
  created() {
    // 初始化加载数据
    this.fetchTableData()
  },
  methods: {
    // 获取状态样式类
    getStatusClass(status) {
      switch(status) {
        case '已通过':
          return 'status-reported';
        case '待审批':
          return 'status-pending';
        case '已驳回':
          return 'status-rejected';
        default:
          return '';
      }
    },
    
    // 获取表格数据
    async fetchTableData() {
      if (this.loading.table) return
      this.loading.table = true
      
      try {
        // 处理时间范围
        if (this.filters.timeRange) {
          // 判断timeRange是数组还是字符串
          if (Array.isArray(this.filters.timeRange)) {
            // daterange组件返回的是数组
            const [start, end] = this.filters.timeRange
            this.filters.createTimeStart = start
            this.filters.createTimeEnd = end
          } else if (typeof this.filters.timeRange === 'string' && this.filters.timeRange.includes(' - ')) {
            // 处理字符串格式的日期范围
          const [start, end] = this.filters.timeRange.split(' - ')
          this.filters.createTimeStart = start
          this.filters.createTimeEnd = end
          } else {
            // 其他情况，清空日期范围
            this.filters.createTimeStart = ''
            this.filters.createTimeEnd = ''
          }
        } else {
          this.filters.createTimeStart = ''
          this.filters.createTimeEnd = ''
        }
        
        const params = {
          ...this.filters,
          pageNum: this.pagination.currentPage,
          pageSize: this.pagination.pageSize
        }
        
        // 从请求参数中删除timeRange
        delete params.timeRange
        
        const res = await getHealthRecordPage(params)
        if (res.data && res.data.list) {
          // 处理返回的数据
          this.tableData = res.data.list.map((item, index) => {
            let statusText = '未知'
            if (item.status === 1) {
              statusText = '待审批'
            } else if (item.status === 2) {
              statusText = '已通过'
            } else if (item.status === 9) {
              statusText = '已驳回'
            }
            
            // 格式化时间
            const reportTime = item.createTime ? this.formatDateTime(item.createTime) : '-'
            
            // 使用fieldContent作为排查情况
            const measureDetail = item.fieldContent || '-'
            
            // 使用measureContent作为管控措施
            const controlMeasure = item.measureContent || '-'
            
            // 获取填报类型
            let fillTypeName = '-'
            if (item.fillType === 1) {
              fillTypeName = '按天'
            } else if (item.fillType === 2) {
              fillTypeName = '按班组'
            } else {
              fillTypeName = item.fillTypeName || '-'
            }
            
            return {
              id: item.id,
              formName: item.formName || '-',
              name: item.userName,
              department: item.departmentName || '-',
              team: item.className || item.teamName || '-',
              fillTypeName,
              healthStatus: item.healthRatingName || '-',
              measureDetail,
              controlMeasure,
              healthScore: item.score || item.totalScore || '-',
              reportStatus: statusText,
              reportTime,
              // 保存原始数据用于详情展示
              ...item
            }
          })
          
          this.pagination.total = res.data.total || 0
        } else {
          this.tableData = []
          this.pagination.total = 0
        }
      } catch (error) {
        console.error('获取健康台账数据失败', error)
        this.$message.error('获取健康台账数据失败')
        this.tableData = []
      } finally {
        this.loading.table = false
      }
    },
    
    // 查看详情
    async handleViewDetail(row) {
      if (this.loading.detail) return
      this.loading.detail = true
      
      try {
        const res = await getHealthFormUserDetail(row.id)
        if (res.data) {
          this.currentDetail = res.data
          this.detailDialogVisible = true
        }
      } catch (error) {
        console.error('获取健康填报详情失败', error)
        this.$message.error('获取健康填报详情失败')
      } finally {
        this.loading.detail = false
      }
    },
    
    // 关闭详情弹窗
    handleCloseDetailDialog() {
      this.detailDialogVisible = false
    },
    
    // 搜索
    handleSearch() {
      this.pagination.currentPage = 1
      this.fetchTableData()
    },
    
    // 重置搜索条件
    handleReset() {
      this.filters = {
        timeRange: '',
        userName: '',
        status: '',
        createTimeStart: '',
        createTimeEnd: '',
        pageNum: 1,
        pageSize: 10
      }
      this.fetchTableData()
    },
    
    // 导出
    handleExport() {
      if (this.loading.export) return
      this.loading.export = true
      
      this.$message.info('正在导出数据，请稍候...')
      
      // 准备导出参数，与查询使用相同的筛选条件
      const params = { ...this.filters }
      
      // 处理时间范围
      if (params.timeRange) {
        if (Array.isArray(params.timeRange)) {
          const [start, end] = params.timeRange
          params.createTimeStart = start
          params.createTimeEnd = end
        } else if (typeof params.timeRange === 'string' && params.timeRange.includes(' - ')) {
          const [start, end] = params.timeRange.split(' - ')
          params.createTimeStart = start
          params.createTimeEnd = end
        }
      }
      
      // 删除不需要的参数
      delete params.timeRange
      
      // 导出为Excel
      import('@/api/health-form-user').then(module => {
        module.exportHealthRecord(params).then(response => {
          try {
            // 检查服务器是否返回了错误信息（以JSON格式）
            if (response.type && response.type.includes('json')) {
              // 尝试解析JSON错误信息
              const reader = new FileReader()
              reader.onload = e => {
                try {
                  const errorJson = JSON.parse(e.target.result)
                  this.$message.error(errorJson.message || '导出失败')
                } catch (parseError) {
                  this.$message.error('导出失败，无法解析错误信息')
                }
                this.loading.export = false
              }
              reader.readAsText(response)
              return
            }
            
            // 处理二进制流
            const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
            
            // 检查Blob是否为空
            if (blob.size === 0) {
              this.$message.error('导出失败，获取到空文件')
              this.loading.export = false
              return
            }
            
            // 创建下载链接
            const link = document.createElement('a')
            link.href = URL.createObjectURL(blob)
            link.download = '健康台账数据.xlsx'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            
        this.$message.success('导出成功')
          } catch (error) {
            console.error('处理导出文件时出错', error)
            this.$message.error('导出失败：' + (error.message || '未知错误'))
          } finally {
            this.loading.export = false
          }
        }).catch(error => {
          console.error('导出失败', error)
          this.$message.error('导出失败：' + (error.message || '服务器处理异常'))
          this.loading.export = false
        })
      }).catch(error => {
        console.error('加载导出模块失败', error)
        this.$message.error('导出功能加载失败')
        this.loading.export = false
      })
    },
    
    // 分页大小变化
    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.filters.pageSize = size
      this.fetchTableData()
    },
    
    // 当前页变化
    handleCurrentChange(page) {
      this.pagination.currentPage = page
      this.filters.pageNum = page
      this.fetchTableData()
    },
    
    // 格式化日期时间为yyyy-MM-dd HH:mm:ss
    formatDateTime(dateTime) {
      if (!dateTime) return '-'
      
      // 如果是时间戳（数字），转换为Date对象
      let date = dateTime
      if (typeof dateTime === 'number') {
        date = new Date(dateTime)
      } else if (typeof dateTime === 'string' && !isNaN(Number(dateTime))) {
        date = new Date(Number(dateTime))
      } else if (typeof dateTime === 'string') {
        date = new Date(dateTime)
      }
      
      if (!(date instanceof Date) || isNaN(date)) {
        return dateTime.toString()
      }
      
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    }
  }
}
</script>

<style scoped>
.health-record-container {
  background-color: #fff;
  padding: 20px;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.page-title {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 20px;
  color: #303133;
}

/* 筛选区域样式 */
.filter-section {
  margin-bottom: 20px;
}

.filter-row {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 10px;
}

.filter-item, .filter-select {
  width: 180px;
}

.date-picker-input {
  width: 320px;
}

.filter-button {
  margin: 0;
}

.spacer {
  flex-grow: 1;
}

/* 表格状态样式 */
.status-normal {
  color: #67C23A;
}

.status-reported {
  color: #67C23A;
}

.status-pending {
  color: #E6A23C;
}

.status-rejected {
  color: #F56C6C;
}

/* 分页容器 */
.pagination-container {
  margin-top: 20px;
  text-align: right;
}

/* 弹窗样式 */
.health-detail-dialog .el-dialog__header {
  padding: 15px 20px;
  border-bottom: 1px solid #e4e7ed;
}

.health-detail-dialog .el-dialog__body {
  padding: 20px;
}

.detail-content {
  padding: 0 10px;
}

.section-title {
  font-size: 16px;
  font-weight: bold;
  margin: 20px 0 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #ebeef5;
}

.section-title:first-child {
  margin-top: 0;
}

.required {
  color: #f56c6c;
  margin-left: 4px;
}

.detail-row {
  display: flex;
  margin-bottom: 15px;
}

.detail-item {
  flex: 1;
  display: flex;
  align-items: baseline;
}

.detail-item.full-width {
  flex: 2;
}

.detail-label {
  width: 80px;
  color: #606266;
  text-align: left;
}

.detail-value {
  flex: 1;
  color: #303133;
  text-align: left;
}

.detail-value.score {
  font-size: 24px;
  font-weight: bold;
  color: #409EFF;
}

.detail-label-top {
  color: #606266;
  text-align: left;
  margin-bottom: 8px;
  display: block;
}

.detail-column {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.detail-textarea {
  flex: 1;
  min-height: 60px;
  padding: 10px;
  background-color: #f7f7f7;
  border-radius: 4px;
  margin-top: 5px;
  color: #303133;
  white-space: pre-wrap;
  word-break: break-all;
  line-height: 1.5;
}

.process-timeline {
  padding: 10px 0;
}

.process-item {
  display: flex;
  margin-bottom: 15px;
  padding-left: 10px;
  position: relative;
}

.process-item:before {
  content: '';
  position: absolute;
  left: 0;
  top: 8px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #409EFF;
}

.process-time {
  width: 180px;
  color: #909399;
}

.process-user {
  width: 100px;
  color: #606266;
}

.process-action {
  color: #303133;
}

/* 响应式调整 */
@media (max-width: 1200px) {
  .filter-row {
    flex-wrap: wrap;
  }
  
  .spacer {
    display: none;
  }
  
  .filter-item, .filter-select {
    width: 100%;
  }
  
  .detail-row {
    flex-direction: column;
  }
  
  .detail-item {
    margin-bottom: 10px;
  }
  
  .process-item {
    flex-direction: column;
  }
  
  .process-time, .process-user {
    width: auto;
    margin-bottom: 5px;
  }
}
</style> 